/**
 * Setup & goal — variant A, the provenance form (spec #47 §3), on the
 * per-agent workspace #44 built. `/agents` stays as the tenant-wide index.
 *
 * THE RULE THIS FILE EXISTS TO KEEP: every row in §3's inventory renders.
 * A field this build cannot edit is shown with its EFFECTIVE VALUE, a lock
 * and the named reason — never hidden, and never a disabled empty shell.
 * Hiding it would make the form look simpler and make the product a liar
 * about what it is doing with the tenant's agent.
 *
 * ONE WRITE PATH. Every editable row here goes through `PUT
 * /api/agents/{domain}/settings` — the same service that validates against
 * the current system ceiling and the same one the resolver layers. The
 * soul fields belong to #42's `PUT /api/agents/{d}/soul` and are LINKED,
 * never merged into this payload (a soul write carrying a leash key is a
 * 422 by #42 §1). Specialist config belongs to §3a's own endpoint.
 *
 * The server stays the authority. `field_policy` (D5) is a courtesy that
 * lets the form say "locked, and here is why" before you touch it; the
 * 422 path is rendered regardless, at the field, with the value you typed
 * still there to correct.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  Checkbox,
  Divider,
  InlineStack,
  Select,
  Text,
  TextField,
} from '@shopify/polaris';

import { planLaneForDomain } from '../../../contexts/workspaceComposition';
import { isApprovedPlan } from '../../../types/plan';
import { useWorkspace } from '../../../hooks/useWorkspace';
import {
  listSettingsAudit,
  updateAgentSettings,
  type SettingsAuditEntry,
} from '../../../services/agentSettingsApi';
import type {
  AgentSettings,
  AgentSettingsUpdate,
  FieldPolicyEntry,
} from '../../../types/agentSettings';
import { getAutonomyLevelLabel } from '../../../types/agentSettings';
import { MiniInterview } from './MiniInterview';
import { ProvenanceTag } from './ProvenanceTag';
import {
  classifyCeiling,
  isWritable,
  resetTargetFor,
  resolveProvenance,
  type CeilingState,
  type FieldAudit,
  type Provenance,
  type Reader,
} from './provenance';

// ---------------------------------------------------------------------------
// Audit -> per-field change rows
// ---------------------------------------------------------------------------

/**
 * `settings_audit` already journals per FIELD (`diff_changes` keys its
 * changes by field name), which is the only per-field signal a client has
 * before D1/D2 land. Flattening it here is what lets a knob with no
 * exposed system value still say something true about itself.
 */
export function flattenAudit(
  entries: readonly SettingsAuditEntry[],
  domain: string
): FieldAudit[] {
  const rows: FieldAudit[] = [];
  entries
    .filter(
      (entry) =>
        entry.surface === 'agent_settings' && entry.object_key === domain
    )
    .forEach((entry) => {
      // Plan provisioning still rides its attribution INSIDE `changes`
      // under `_source`, behind step 5's marked seam ("when the columns
      // land, this call site moves the two keys out of changes"). Until
      // that closes, this is where a plan-set field's proposal id lives —
      // and `_source` is emphatically not a field anyone can edit, so it
      // must never become a row.
      const source = entry.changes?._source as
        | { proposal_id?: string | null }
        | undefined;
      Object.entries(entry.changes ?? {}).forEach(([field, change]) => {
        if (field === '_source') return;
        rows.push({
          field,
          old: change.old,
          new: change.new,
          changed_by: entry.changed_by,
          author_role: entry.author_role ?? null,
          // The real D2 column WINS the moment it is populated; the blob
          // is only the fallback for the shape main carries today.
          source_proposal_id:
            entry.source_proposal_id ?? source?.proposal_id ?? null,
          created_at: entry.created_at,
        });
      });
    });
  return rows;
}

// ---------------------------------------------------------------------------
// Row scaffolding
// ---------------------------------------------------------------------------

interface RowProps {
  label: string;
  provenance?: Provenance | null;
  ceiling?: CeilingState;
  policy?: FieldPolicyEntry;
  isOperator: boolean;
  /** Rendered under the control: the floor, in the platform's own words. */
  floor?: string | null;
  refusal?: string | null;
  children?: React.ReactNode;
  /** Heading suppressed when the control's own label carries the name. */
  labelledByControl?: boolean;
  actions?: React.ReactNode;
}

function lockChip(ceiling: CeilingState, policy?: FieldPolicyEntry) {
  switch (ceiling) {
    case 'plan_locked':
      return (
        <Badge tone="attention">
          {policy?.required_plan
            ? `needs the ${policy.required_plan} plan`
            : 'needs a higher plan'}
        </Badge>
      );
    case 'operator_only':
      return <Badge tone="attention">operator</Badge>;
    case 'read_only_v1':
      return <Badge>read-only in v1</Badge>;
    default:
      return null;
  }
}

function SetupRow({
  label,
  provenance,
  ceiling = 'editable',
  policy,
  isOperator,
  floor,
  refusal,
  children,
  labelledByControl = false,
  actions,
}: RowProps) {
  return (
    <BlockStack gap="150">
      <InlineStack gap="200" blockAlign="center" wrap>
        {!labelledByControl && (
          <Text as="h3" variant="headingXs">
            {label}
          </Text>
        )}
        {lockChip(ceiling, policy)}
        {provenance ? <ProvenanceTag provenance={provenance} /> : null}
      </InlineStack>
      {children}
      {floor ? (
        <Text as="p" variant="bodySm" tone="subdued">
          {floor}
        </Text>
      ) : null}
      {/* §4.3 — the floors in code do not branch on role, and the copy
          says so rather than letting an operator discover it at the 422. */}
      {isOperator && isWritable(ceiling) && floor ? (
        <Text as="p" variant="bodySm" tone="subdued">
          Platform floor — operator included.
        </Text>
      ) : null}
      {refusal ? (
        <Banner tone="critical">{refusal}</Banner>
      ) : null}
      {actions ? <InlineStack gap="200">{actions}</InlineStack> : null}
    </BlockStack>
  );
}

// ---------------------------------------------------------------------------
// The tab
// ---------------------------------------------------------------------------

const AUTONOMY_OPTIONS: AgentSettings['autonomy_level'][] = [
  'manual',
  'auto_tier1',
];

export function SetupGoalTab({ domain }: { domain: string }) {
  const { agents, plan } = useWorkspace();
  const contextAgent = agents.find((a) => a.domain === domain) ?? null;

  const [written, setWritten] = useState<AgentSettings | null>(null);
  const [audits, setAudits] = useState<FieldAudit[]>([]);
  const [refusals, setRefusals] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);
  // §2's per-agent opening. Offered once, to an agent that is on and has
  // never been tuned — a new lane from an amendment, or one turned on
  // from the enablement board. Dismissible, and never a second store.
  const [miniDone, setMiniDone] = useState(false);

  const agent = written ?? contextAgent;

  useEffect(() => {
    let cancelled = false;
    // `Promise.resolve` rather than a bare `.then`: the change trail is
    // the one read this tab treats as optional, and a caller (or a suite)
    // that has not wired it must degrade to "source unrecorded", not
    // crash the form.
    Promise.resolve(listSettingsAudit(100))
      .then((response) => {
        if (!cancelled) setAudits(flattenAudit(response?.entries ?? [], domain));
      })
      .catch(() => {
        // The change trail is auxiliary. Losing it costs attribution
        // fidelity — every non-default field falls back to "source
        // unrecorded" — but it never blocks the form.
        if (!cancelled) setAudits([]);
      });
    return () => {
      cancelled = true;
    };
  }, [domain]);

  // Draft state per field, seeded from the effective value.
  const [exploration, setExploration] = useState<string | null>(null);
  const [autonomy, setAutonomy] = useState<string | null>(null);
  const [monthly, setMonthly] = useState<string | null>(null);
  const [caps, setCaps] = useState<Record<string, string> | null>(null);
  const [actionTypes, setActionTypes] = useState<string[] | null>(null);
  const [platforms, setPlatforms] = useState<string[] | null>(null);

  const lane = planLaneForDomain(plan, domain);
  const policy = agent?.field_policy ?? {};
  const reader: Reader = {
    role: agent?.actor?.role ?? 'tenant',
    user_id: agent?.actor?.user_id ?? null,
  };
  const isOperator = reader.role === 'operator';

  const auditsFor = useCallback(
    (field: string) => audits.filter((row) => row.field === field),
    [audits]
  );

  const planSet = useCallback(
    (field: string) =>
      (lane?.plan_set_fields ?? []).some(
        (name) => name === field || name.endsWith(`.${field}`)
      ),
    [lane]
  );

  const provenanceFor = useCallback(
    (
      field: string,
      effective: unknown,
      systemValue?: unknown,
      systemValueKnown = systemValue !== undefined
    ): Provenance =>
      resolveProvenance({
        effective,
        systemValue,
        systemValueKnown,
        sourceIsSystem: agent?.source === 'system',
        audits: auditsFor(field),
        planSet: planSet(field),
        planVersion:
          plan && isApprovedPlan(plan) ? plan.version : null,
        reader,
      }),
    // `reader` is derived from `agent`, which is already a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [agent, auditsFor, plan, planSet]
  );

  const save = useCallback(
    async (field: string, update: AgentSettingsUpdate) => {
      if (!agent) return;
      setSaving(field);
      setRefusals((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
      try {
        const updated = await updateAgentSettings(agent.domain, update);
        if (updated) setWritten(updated);
        const response = await Promise.resolve(listSettingsAudit(100)).catch(
          () => null
        );
        if (response) setAudits(flattenAudit(response.entries, agent.domain));
      } catch (err) {
        // The refusal renders AT THE FIELD and the draft is left exactly
        // as typed — correcting a rejected number should not mean
        // retyping it from memory.
        const detail =
          (err as Error & { detail?: string }).detail ||
          (err instanceof Error ? err.message : 'Could not save that.');
        setRefusals((prev) => ({ ...prev, [field]: detail }));
      } finally {
        setSaving(null);
      }
    },
    [agent]
  );

  const ceilingFor = (field: string) => classifyCeiling(policy[field]);

  const resetRow = useCallback(
    (field: string, toUpdate: (value: unknown) => AgentSettingsUpdate) => {
      if (!agent) return;
      const target = resetTargetFor(field, { agent, audits: auditsFor(field) });
      if (!target.known) return;
      void save(field, toUpdate(target.value));
    },
    [agent, auditsFor, save]
  );

  const canReset = (field: string) =>
    agent
      ? resetTargetFor(field, { agent, audits: auditsFor(field) }).known
      : false;

  const capFloors = useMemo(
    () =>
      (policy.safety_caps?.floor ?? {}) as Record<
        string,
        { platform_limit: number; direction: string; label: string }
      >,
    [policy.safety_caps]
  );

  if (!agent) {
    return (
      <Card>
        <Text as="p" tone="subdued">
          Loading this agent&apos;s setup…
        </Text>
      </Card>
    );
  }

  const explorationValue =
    exploration ?? String(Math.round(agent.exploration_fraction * 100));
  const autonomyValue = autonomy ?? agent.autonomy_level;
  const monthlyValue =
    monthly ??
    (agent.budget_caps.monthly_usd != null
      ? String(agent.budget_caps.monthly_usd)
      : '');
  const capValues =
    caps ??
    Object.fromEntries(
      Object.keys(capFloors).map((key) => [
        key,
        agent.safety_caps[key] != null ? String(agent.safety_caps[key]) : '',
      ])
    );
  const actionValues = actionTypes ?? agent.allowed_action_types;
  const platformValues = platforms ?? agent.allowed_platforms;

  const saveButton = (field: string, label: string) => (
    <Button
      accessibilityLabel={`Save ${label}`}
      loading={saving === field}
      onClick={() => void saveRow(field)}
    >
      Save
    </Button>
  );

  const resetButton = (
    field: string,
    label: string,
    toUpdate: (value: unknown) => AgentSettingsUpdate
  ) =>
    canReset(field) ? (
      <Button
        variant="plain"
        accessibilityLabel={`Reset ${label} to the platform value`}
        onClick={() => resetRow(field, toUpdate)}
      >
        Reset to platform value
      </Button>
    ) : null;

  function saveRow(field: string) {
    switch (field) {
      case 'exploration_fraction':
        return save(field, {
          exploration_fraction: Number(explorationValue) / 100,
        });
      case 'autonomy_level':
        return save(field, {
          autonomy_level: autonomyValue as AgentSettings['autonomy_level'],
        });
      case 'budget_caps':
        return save(field, {
          budget_caps: {
            ...agent!.budget_caps,
            monthly_usd: monthlyValue === '' ? null : Number(monthlyValue),
          },
        });
      case 'safety_caps':
        return save(field, {
          safety_caps: Object.fromEntries(
            Object.entries(capValues)
              .filter(([, v]) => v !== '')
              .map(([k, v]) => [k, Number(v)])
          ),
        });
      case 'allowed_action_types':
        return save(field, { allowed_action_types: actionValues });
      case 'allowed_platforms':
        return save(field, { allowed_platforms: platformValues });
      default:
        return undefined;
    }
  }

  const offerMiniInterview =
    !miniDone && agent.enabled && agent.source === 'system';

  return (
    <BlockStack gap="400">
      {offerMiniInterview && (
        <MiniInterview
          agent={agent}
          onDone={() => setMiniDone(true)}
        />
      )}

      {isOperator && (
        <Banner tone="warning">
          <Text as="p">
            You are acting as an operator in this workspace, as yourself.
            Every change is recorded against your user.
          </Text>
        </Banner>
      )}

      <Card>
        <BlockStack gap="400">
          {/* --- the goal, which is not editable here ------------------- */}
          <SetupRow
            label="Workspace goal · this agent's lane"
            ceiling="read_only_v1"
            isOperator={isOperator}
          >
            {lane ? (
              <BlockStack gap="050">
                <Text as="p" fontWeight="medium">
                  {lane.display_name}
                </Text>
                {lane.mandate ? (
                  <Text as="p" variant="bodySm">
                    {lane.mandate}
                    {lane.cadence ? ` · ${lane.cadence}` : ''}
                  </Text>
                ) : null}
              </BlockStack>
            ) : (
              <Text as="p" tone="subdued">
                No lane in the current plan names this agent.
              </Text>
            )}
            <Text as="p" variant="bodySm" tone="subdued">
              Changing the goal proposes a new plan version; nothing changes
              until you approve it.{' '}
              <Link to="/welcome">Change it in the Plan</Link>
            </Text>
          </SetupRow>

          <Divider />

          {/* --- the leash --------------------------------------------- */}
          <SetupRow
            label="Agent on/off"
            ceiling={ceilingFor('enabled')}
            policy={policy.enabled}
            isOperator={isOperator}
            provenance={provenanceFor('enabled', agent.enabled)}
            refusal={refusals.enabled}
            actions={
              <Button
                accessibilityLabel="Save Agent on/off"
                loading={saving === 'enabled'}
                onClick={() =>
                  void save('enabled', { enabled: !agent.enabled })
                }
              >
                {agent.enabled ? 'Turn off' : 'Turn on'}
              </Button>
            }
          >
            <Badge tone={agent.enabled ? 'success' : undefined}>
              {agent.enabled ? 'On' : 'Off'}
            </Badge>
          </SetupRow>

          <SetupRow
            label="Autonomy"
            labelledByControl
            ceiling={ceilingFor('autonomy_level')}
            policy={policy.autonomy_level}
            isOperator={isOperator}
            provenance={provenanceFor('autonomy_level', agent.autonomy_level)}
            floor={policy.autonomy_level?.reason}
            refusal={refusals.autonomy_level}
            actions={saveButton('autonomy_level', 'Autonomy')}
          >
            <Select
              label="Autonomy"
              options={AUTONOMY_OPTIONS.map((level) => ({
                label: getAutonomyLevelLabel(level),
                value: level,
              }))}
              value={autonomyValue}
              disabled={!isWritable(ceilingFor('autonomy_level'))}
              onChange={setAutonomy}
            />
          </SetupRow>

          <SetupRow
            label="Exploration"
            labelledByControl
            ceiling={ceilingFor('exploration_fraction')}
            policy={policy.exploration_fraction}
            isOperator={isOperator}
            provenance={provenanceFor(
              'exploration_fraction',
              agent.exploration_fraction
            )}
            floor={policy.exploration_fraction?.reason}
            refusal={refusals.exploration_fraction}
            actions={saveButton('exploration_fraction', 'Exploration')}
          >
            <TextField
              label="Exploration"
              type="number"
              suffix="%"
              autoComplete="off"
              value={explorationValue}
              disabled={!isWritable(ceilingFor('exploration_fraction'))}
              onChange={setExploration}
            />
          </SetupRow>

          <SetupRow
            label="Safety caps"
            ceiling={ceilingFor('safety_caps')}
            policy={policy.safety_caps}
            isOperator={isOperator}
            provenance={provenanceFor('safety_caps', agent.safety_caps)}
            floor={policy.safety_caps?.reason}
            refusal={refusals.safety_caps}
            actions={saveButton('safety_caps', 'Safety caps')}
          >
            {Object.entries(capFloors).map(([key, floor]) => (
              <TextField
                key={key}
                label={floor.label}
                type="number"
                autoComplete="off"
                value={capValues[key] ?? ''}
                disabled={!isWritable(ceilingFor('safety_caps'))}
                helpText={
                  floor.direction === 'max'
                    ? `Platform limit: at most ${floor.platform_limit}.`
                    : `Platform minimum: at least ${floor.platform_limit}.`
                }
                onChange={(value) =>
                  setCaps({ ...capValues, [key]: value })
                }
              />
            ))}
          </SetupRow>

          <SetupRow
            label="Monthly envelope"
            labelledByControl
            ceiling={ceilingFor('budget_caps')}
            policy={policy.budget_caps}
            isOperator={isOperator}
            provenance={provenanceFor('budget_caps', agent.budget_caps)}
            floor={policy.budget_caps?.reason}
            refusal={refusals.budget_caps}
            actions={saveButton('budget_caps', 'Monthly envelope')}
          >
            <TextField
              label="Monthly envelope"
              type="number"
              prefix="$"
              autoComplete="off"
              placeholder="No envelope declared"
              value={monthlyValue}
              disabled={!isWritable(ceilingFor('budget_caps'))}
              onChange={setMonthly}
            />
            <Badge tone="attention">declared — not yet enforced</Badge>
          </SetupRow>

          <SetupRow
            label="May do"
            ceiling={ceilingFor('allowed_action_types')}
            policy={policy.allowed_action_types}
            isOperator={isOperator}
            provenance={provenanceFor(
              'allowed_action_types',
              agent.allowed_action_types,
              agent.system_allowed_action_types
            )}
            floor={policy.allowed_action_types?.reason}
            refusal={refusals.allowed_action_types}
            actions={
              <>
                {saveButton('allowed_action_types', 'May do')}
                {resetButton('allowed_action_types', 'May do', (value) => ({
                  allowed_action_types: value as string[],
                }))}
              </>
            }
          >
            {/* The platform ceiling is the list; an entry this tenant
                narrowed away stays visible and unticked, so what was given
                up is legible rather than simply missing. */}
            {agent.system_allowed_action_types.map((key) => (
              <Checkbox
                key={key}
                label={key}
                checked={actionValues.includes(key)}
                disabled={!isWritable(ceilingFor('allowed_action_types'))}
                onChange={(checked) =>
                  setActionTypes(
                    checked
                      ? [...actionValues, key]
                      : actionValues.filter((k) => k !== key)
                  )
                }
              />
            ))}
          </SetupRow>

          <SetupRow
            label="May act on"
            ceiling={ceilingFor('allowed_platforms')}
            policy={policy.allowed_platforms}
            isOperator={isOperator}
            provenance={provenanceFor(
              'allowed_platforms',
              agent.allowed_platforms,
              agent.system_allowed_platforms
            )}
            floor={policy.allowed_platforms?.reason}
            refusal={refusals.allowed_platforms}
            actions={
              <>
                {saveButton('allowed_platforms', 'May act on')}
                {resetButton('allowed_platforms', 'May act on', (value) => ({
                  allowed_platforms: value as string[],
                }))}
              </>
            }
          >
            {agent.system_allowed_platforms.map((key) => (
              <Checkbox
                key={key}
                label={key}
                checked={platformValues.includes(key)}
                disabled={!isWritable(ceilingFor('allowed_platforms'))}
                onChange={(checked) =>
                  setPlatforms(
                    checked
                      ? [...platformValues, key]
                      : platformValues.filter((k) => k !== key)
                  )
                }
              />
            ))}
          </SetupRow>

          {/* --- read-only in v1, each with its own named reason -------- */}
          <SetupRow
            label="May measure"
            ceiling="read_only_v1"
            policy={policy.allowed_proxy_metrics}
            isOperator={isOperator}
            floor={policy.allowed_proxy_metrics?.reason}
          >
            <Text as="p">
              {(agent.system_allowed_proxy_metrics ?? []).join(', ') || 'none'}
            </Text>
          </SetupRow>

          <SetupRow
            label="Owns (tools)"
            ceiling="read_only_v1"
            isOperator={isOperator}
            floor="Claimed, not yet enforced — tool ownership arrives with its own step."
          >
            <Text as="p" tone="subdued">
              {agent.owned_questions[0] ?? 'Nothing claimed yet.'}
            </Text>
          </SetupRow>

          <SetupRow
            label="Model"
            ceiling="read_only_v1"
            isOperator={isOperator}
            floor="Set by your plan and tier; a per-agent override arrives with step 8."
          />

          <SetupRow
            label="Voice · charter · rules you set"
            ceiling="read_only_v1"
            isOperator={isOperator}
          >
            <Text as="p" variant="bodySm" tone="subdued">
              These live on this agent&apos;s soul, which has its own write
              path — a soul change is never merged into a settings save.{' '}
              <Link to={`/marketing-agents/a/${domain}?tab=soul`}>
                Open Soul &amp; memory
              </Link>
            </Text>
          </SetupRow>
        </BlockStack>
      </Card>
    </BlockStack>
  );
}

export default SetupGoalTab;
